import { useState, useCallback } from 'react';
import { DragStartEvent, DragEndEvent, DragCancelEvent } from '@dnd-kit/core';
import { TreeFolder } from '../services';

export type DraggedItemType = 'file' | 'folder';

export interface DraggedItem {
  id: number;
  type: DraggedItemType;
  name: string;
}

interface DraggedItemData {
  item: DraggedItem;
  selectedItems: DraggedItem[];
}

interface DropTarget {
  folder: TreeFolder;
}

interface FileDragAndDropState {
  activeItem: DraggedItem | null;
  droppedItems: DraggedItem[];
  targetFolder: TreeFolder | null;
}

interface FileDragAndDropHandlers {
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: (event: DragCancelEvent) => void;
  clearDropState: () => void;
}

type UseFileDragAndDropReturn = FileDragAndDropState & FileDragAndDropHandlers;

export function useFileDragAndDrop(): UseFileDragAndDropReturn {
  const [activeItem, setActiveItem] = useState<DraggedItem | null>(null);
  const [droppedItems, setDroppedItems] = useState<DraggedItem[]>([]);
  const [targetFolder, setTargetFolder] = useState<TreeFolder | null>(null);

  const onDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current as DraggedItemData | undefined;

    if (data?.item) {
      setActiveItem(data.item);
    }
  }, []);

  const onDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveItem(null);
      return;
    }

    const activeData = active.data.current as DraggedItemData | undefined;
    const overData = over.data.current as DropTarget | undefined;

    if (activeData?.item && overData?.folder) {
      // Standard OS behaviour: if the grabbed item is part of the current
      // selection move the whole selection; otherwise move only the grabbed item.
      const isDraggedSelected = activeData.selectedItems.some(
        (item) => item.id === activeData.item.id && item.type === activeData.item.type,
      );
      const itemsToMove = isDraggedSelected
        ? activeData.selectedItems
        : [activeData.item];

      // Filter out the target folder (can't move a folder into itself)
      const filteredItems = itemsToMove.filter(
        (item) => !(item.type === 'folder' && item.id === overData.folder.id),
      );

      if (filteredItems.length > 0) {
        setDroppedItems(filteredItems);
        setTargetFolder(overData.folder);
      }
    }

    setActiveItem(null);
  }, []);

  const onDragCancel = useCallback(() => {
    setActiveItem(null);
  }, []);

  const clearDropState = useCallback(() => {
    setDroppedItems([]);
    setTargetFolder(null);
  }, []);

  return {
    activeItem,
    droppedItems,
    targetFolder,
    onDragStart,
    onDragEnd,
    onDragCancel,
    clearDropState,
  };
}
